import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { FiArrowLeft, FiCopy } from "react-icons/fi";
import { FaPaperPlane } from "react-icons/fa";
import DOMPurify from "dompurify";
import axios from "axios";
import { motion } from "framer-motion"; // Анимация
import { sendProductToTelegram } from "../services/telegramService";
import ChatButton from "../components/ChatButton";

const API_KEY = "d0d0e4b5f11eccb0cab298bbb66068273ab5a3d145a993577cc9a94a761273f6";

export default function CategoriesDetails({ products, categories, telegramUserId  }) {
  const { id } = useParams();
  const navigate = useNavigate();
  const [categoryProducts, setCategoryProducts] = useState([]);
  const [categoryName, setCategoryName] = useState("");
  const [usageVisible, setUsageVisible] = useState({});

  useEffect(() => {
    if (products.length > 0 && categories.length > 0) {
      const category = categories.find((c) => c.id == id);
      if (category) {
        setCategoryName(category.name);
        const filteredProducts = products
          .filter((p) => p.category_id == id)
          .sort((a, b) => b.offers_count - a.offers_count);

        setCategoryProducts(filteredProducts);
      }
    }
  }, [products, categories, id]);

  return (
    <div className="min-h-screen bg-gradient-to-b from-[#0D1B2A] to-[#1B2433] text-white flex flex-col items-center pb-24 px-2">
      {/* Верхний navbar */}
      <div className="w-full fixed top-0 left-0 bg-[#1B2433] shadow-lg px-4 py-3 flex items-center z-50">
        <button
          onClick={() => navigate(-1)}
          className="h-14 w-14 bg-[#222C40] rounded-lg flex items-center justify-center hover:bg-opacity-40 transition shadow-lg mr-3"
        >
          <FiArrowLeft size={24} />
        </button>

        <div className="flex-1 flex items-center justify-center py-3 px-4 rounded-lg shadow-lg h-14 bg-[#222C40]">
          <h1 className="text-lg font-bold text-white">{categoryName}</h1>
        </div>
      </div>

      {/* Если нет товаров в категории */}
      {categoryProducts.length === 0 ? (
        <div className="flex flex-col items-center justify-center h-screen -mt-20">
          <div className="bg-[#222A3A] bg-opacity-80 px-6 py-4 rounded-lg shadow-md text-center">
            <h2 className="text-xl font-semibold text-gray-300">😞 В текущий момент</h2>
            <h3 className="text-lg text-gray-400">в данной категории нет предложений</h3>
          </div>
          <button
            onClick={() => navigate(-1)}
            className="mt-6 px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white 
            rounded-lg shadow-lg text-lg font-semibold transition duration-200"
          >
            Вернуться назад
          </button>
        </div>
      ) : (
        // Контейнер для карточек товаров
        <div className="mt-24 w-[96%] max-w-md space-y-4">
          {categoryProducts.map((product, index) => (
            <motion.div
              key={product.id}
              className="bg-gradient-to-b from-[#1B2433] to-[#2D415A] rounded-xl shadow-2xl overflow-hidden p-4 mx-auto border border-gray-600"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: index * 0.1, duration: 0.4 }}
            >
              {/* Основное изображение */}
              <div className="rounded-t-xl overflow-hidden shadow-md">
                <img
                  src={`https://xalavach.ru/api/image.php?file=${product.main_image}`}
                  alt={product.name}
                  className="w-full object-cover"
                />
              </div>

              {/* Блок с названием, датой */}
              <div className="p-4 flex justify-between items-center">
                <h2 className="text-lg font-bold">{product.name}</h2>
                <div className="bg-yellow-400 text-black px-3 py-1 rounded-lg shadow-md text-sm">
                  До {formatDate(product.expiry_date)}
                </div>
              </div>

              {/* Детали карточки */}
              <div className="p-5 bg-gradient-to-b from-[#253547] to-[#34495E] rounded-xl shadow-md border border-gray-600 backdrop-blur-md">
                <p className="text-md text-gray-300">{product.description}</p>

                {/* Промокод */}
                <div className="mt-5 p-4 bg-gray-900 text-white rounded-xl flex items-center justify-between shadow-lg border border-gray-700">
                  <div className="flex flex-col">
                    <span className="text-sm text-gray-400">Твой промокод:</span>
                    <span className="font-bold text-xl tracking-wide bg-clip-text text-transparent bg-gradient-to-r from-blue-400 to-blue-600">
                      {product.promo_code}
                    </span>
                  </div>
                  <button
                    className="bg-gradient-to-r from-blue-500 to-blue-700 hover:from-blue-600 hover:to-blue-800 text-white px-4 py-2 rounded-full flex items-center transition duration-200 shadow-md"
                    onClick={() => navigator.clipboard.writeText(product.promo_code)}
                  >
                    <FiCopy size={22} />
                  </button>
                </div>

                {/* Кнопка "Скопировать и перейти на сайт" */}
                {product.website_link && (
                  <button
                    onClick={() => {
                      navigator.clipboard.writeText(product.promo_code);
                      window.open(product.website_link, "_blank");
                    }}
                    className="w-full mt-4 bg-gradient-to-r from-purple-500 to-purple-700 hover:from-purple-600 hover:to-purple-800 
                               text-white py-3 rounded-lg text-lg font-semibold transition duration-200 
                               shadow-[0px_4px_0px_#7b2cbf] hover:shadow-[0px_2px_0px_#5a1a87] 
                               transform active:translate-y-1"
                  >
                    Скопировать и перейти на сайт
                  </button>
                )}

                {/* Кнопка "Как использовать?" */}
                <div className="mt-6">
                  <button
                    onClick={() => setUsageVisible((prev) => ({ ...prev, [product.id]: !prev[product.id] }))}
                    className="w-full text-center text-white bg-gradient-to-r from-blue-500 to-blue-700 py-3 rounded-lg shadow-lg text-lg font-semibold transition duration-200 hover:from-blue-600 hover:to-blue-800 px-4"
                  >
                    Как использовать?
                  </button>
                  {usageVisible[product.id] && (
                    <div className="mt-3 bg-gray-900 p-4 rounded-lg shadow-md text-gray-300 break-words overflow-hidden">
                      <p dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(product.usage_rules) }} />
                    </div>
                  )}
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      )}

      {/* Нижний navbar с кнопкой "Отправить в чат" */}

      {/* Нижний navbar с кнопкой "Отправить в чат" */}
      <ChatButton brandProducts={categoryProducts} telegramUserId={telegramUserId} />
    </div>
  );
}

// Функция форматирования даты
function formatDate(dateStr) {
  const months = [
    "января", "февраля", "марта", "апреля", "мая", "июня",
    "июля", "августа", "сентября", "октября", "ноября", "декабря"
  ];
  const [year, month, day] = dateStr.split("-");
  return `${parseInt(day)} ${months[parseInt(month) - 1]}`;
}
